import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { conversations, people } from "../../data/demoContent";
import { useDemo } from "../../context/DemoContext";
import { ChatBubble } from "./ChatBubble";
import { GlassActionCard } from "./GlassActionCard";

export const ChatView = ({ personId, onBack, onAiAction }) => {
  const { sent, sendMessage, highlight, markInteraction } = useDemo();
  const [text, setText] = useState("");
  const listRef = useRef(null);
  const replyRef = useRef(null);
  const p = people[personId];
  const messages = [...conversations[personId].messages, ...(sent[personId] || [])];

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (highlight === "storyReply" && replyRef.current) {
      list.scrollTo({ top: replyRef.current.offsetTop - 12, behavior: "smooth" });
    } else {
      list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
    }
  }, [personId, messages.length, highlight]);

  const submit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    sendMessage(personId, t);
    setText("");
    markInteraction();
    toast("Sample message added", { description: "This is a demo — nothing is sent." });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="chat-view" data-person={personId}>
      <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-3 pb-3">
        <button type="button" onClick={onBack} aria-label="Back to messages" data-testid="chat-back-button" className="grid h-8 w-8 place-items-center rounded-full text-white/80 hover:bg-white/10">
          <ChevronLeft size={20} />
        </button>
        <img src={p.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-white" data-testid="chat-header-name">{p.name}</p>
          <p className="text-[11px] text-[#A5E6E3]">Active now</p>
        </div>
        <span className="text-[10px] text-white/55">Fictional demo</span>
      </div>
      <div ref={listRef} className="relative flex-1 space-y-2.5 overflow-y-auto px-3 py-3" data-testid="chat-messages">
        {messages.map((m) =>
          m.type === "ai" ? (
            <GlassActionCard key={m.id} card={m} onAction={() => onAiAction(m)} />
          ) : (
            <ChatBubble
              key={m.id}
              message={m}
              ref={m.type === "storyReply" ? replyRef : undefined}
              highlight={highlight === "storyReply" && m.type === "storyReply"}
            />
          )
        )}
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 px-3 pb-4 pt-2">
        <label htmlFor="chat-input" className="sr-only">Reply as Karna (demo)</label>
        <input
          id="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Reply to ${p.name} (demo)`}
          data-testid="chat-input"
          className="h-11 min-w-0 flex-1 rounded-full border border-white/10 bg-white/[0.07] px-4 text-[14px] text-white placeholder:text-white/55 focus:border-[#A79DE2]/60 focus:outline-none"
        />
        <button type="submit" aria-label="Send sample message" data-testid="chat-send-button" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#7866E8] to-[#7398E7] text-white disabled:opacity-40" disabled={!text.trim()}>
          <ArrowUp size={18} />
        </button>
      </form>
    </div>
  );
};
